/**
 * Provides an administrative interface for validating user eligibility
 * and access requirements across integrated systems. Allows administrators
 * to select users by user type, review eligibility checks, and verify
 * user-specific access configurations such as Fluxx account availability.
 * The component supports user discovery, eligibility assessment workflows,
 * and integration-specific validation to help streamline onboarding,
 * provisioning, and access management processes.
 */
import React, { useEffect, useState } from "react";
import { getMembersByUserType, getUserTypes } from "../../services/accessService";
import FluxxConfig from "./FluxxConfig";



const UserEligibility: React.FC = () => {
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState<string | null>(null);

  const [userTypes, setUserTypes] = useState<any[]>([]);
  const [selectedUserType, setSelectedUserType] = useState("");

  const [members, setMembers] = useState<any[]>([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  const items = [
    {
      key: "Fluxx",
      label: "Fluxx User Eligibility",
    },
  ];

  const filtered = items.filter((i) =>
    i.label.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const loadUserTypes = async () => {
      try {
        const response = await getUserTypes();
        setUserTypes(response || []);
      } catch (error) {
        console.error(
          "Failed to load user types",
          error
        );
      }
    };

    loadUserTypes();
  }, []);

  useEffect(() => {
    const loadMembers = async () => {
      if (!selectedUserType) {
        setMembers([]);
        return;
      }

      try {
        setLoadingMembers(true);

        const response =
          await getMembersByUserType(
            Number(selectedUserType)
          );

        setMembers(response || []);
      } catch (error) {
        console.error(
          "Failed to load members",
          error
        );

        setMembers([]);
      } finally {
        setLoadingMembers(false);
      }
    };

    loadMembers();
  }, [selectedUserType]);

  return (
    <div className="bg-[#f6f4ef] min-h-screen font-sans">
      <div className="p-5">
        <div className="bg-white border border-gray-200 rounded-md">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center">
            <div className="flex-1">
              <h2 className="text-md font-semibold text-gray-800">
                User Eligibility
              </h2>
            </div>
          </div>

          <div className="p-5">
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                User Type
              </label>

              <select
                value={selectedUserType}
                onChange={(e) => {
                  setSelectedUserType(
                    e.target.value
                  );

                  setExpanded(null);
                }}
                className="w-full border border-gray-200 rounded-md p-2 focus:outline-none focus:ring-2 focus:ring-orange-400"
              >
                <option value="">
                  Select User Type
                </option>

                {userTypes.map((type) => (
                  <option
                    key={type.id}
                    value={type.id}
                  >
                    {type.user_type}
                  </option>
                ))}
              </select>
            </div>

            <input
              type="text"
              placeholder="Search..."
              disabled={!selectedUserType}
              className="w-full border border-gray-200 p-2 rounded mb-4 focus:outline-none focus:ring-2 focus:ring-orange-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
            />

            {loadingMembers && (
              <div className="text-sm text-gray-500 mb-4">
                Loading members...
              </div>
            )}

            <div className="space-y-3">
              {filtered.map((item) => {
                const isExpanded =
                  expanded === item.key;

                return (
                  <div
                    key={item.key}
                    className="border border-gray-200 rounded-md bg-white"
                  >
                    <div className="p-4 flex justify-between items-center">
                      <div className="text-sm font-medium text-gray-800">
                        {item.label}
                      </div>

                      <button
                        disabled={!selectedUserType}
                        className="bg-[var(--color-brand)] text-white px-3 py-1.5 rounded-md text-sm disabled:bg-gray-300 disabled:cursor-not-allowed"
                        onClick={() =>
                          setExpanded(
                            isExpanded
                              ? null
                              : item.key
                          )
                        }
                      >
                        {isExpanded
                          ? "Close"
                          : "Check"}
                      </button>
                    </div>

                    {isExpanded &&
                      item.key === "Fluxx" && (
                        <div className="border-t border-gray-200 p-4 bg-[#fbfaf7]">
                          <FluxxConfig
                            members={members}
                          />
                        </div>
                      )}
                  </div>
                );
              })}

              {filtered.length === 0 && (
                <div className="text-sm text-gray-500 text-center py-6">
                  No items found
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserEligibility;